import React, { useEffect, useState } from 'react';
import { getMembersDB } from '../../services/db';
import { UserProfile } from '../../types';
import { getSilatRankFromBengkung } from '../../data/bengkungRanks';
import { X, ZoomIn, Award, Shield, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

export interface OfficerItem {
  id?: string;
  name: string;
  role: string;
  bengkung: string;
  bengkungBadge?: string;
  pangkat: string;
  photoUrl: string;
  initials: string;
  titles?: string;
  gelanggang?: string;
}

export const OrgChartPage: React.FC = () => {
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedOfficerIndex, setSelectedOfficerIndex] = useState<number | null>(null);

  const loadData = () => {
    // Read from localStorage cache first for sub-second render
    const cached = localStorage.getItem('asmm_db_members_v4');
    if (cached) {
      try {
        setMembers(JSON.parse(cached));
        setIsLoading(false);
      } catch { }
    }

    // Silent background revalidation
    getMembersDB().then((data) => {
      setMembers(data);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    loadData();
    window.addEventListener('asmm_db_updated', loadData);
    return () => window.removeEventListener('asmm_db_updated', loadData);
  }, []);



  // Helper function to extract initials safely
  const getInitials = (name: string): string => {
    if (!name) return '??';
    return name
      .split(' ')
      .map((n) => n[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Helper to dynamically fetch member by role with fallback
  const getMemberByRole = (
    roleKeys: string[],
    defaultName: string,
    defaultInitials: string,
    defaultPhoto: string,
    defaultTitles?: string,
    defaultBengkung?: string
  ): OfficerItem => {
    const matched = members.find((m) =>
      roleKeys.some((rk) => m.role.trim().toLowerCase() === rk.trim().toLowerCase())
    );

    const name = matched ? matched.name : defaultName;
    const nameLower = name.toLowerCase();
    let bengkungCode = matched?.bengkung || defaultBengkung || 'putih0';
    let pangkatTitle = getSilatRankFromBengkung(bengkungCode);

    // Auto rank rules:
    // Pengerusi auto Rank as Guru Muda (not Jurulatih Kanan)
    if (nameLower.includes('hafife') || roleKeys.some((rk) => rk.trim().toLowerCase() === 'pengerusi')) {
      pangkatTitle = 'Guru Muda';
    } else if (
      nameLower.includes('fauzi') ||
      nameLower.includes('hasbullah') ||
      nameLower.includes('fikriz') ||
      nameLower.includes('shakir') ||
      nameLower.includes('zulkifli')
    ) {
      pangkatTitle = 'Jurulatih Kanan';
      bengkungCode = 'kuning5';
    } else if (nameLower.includes('aizat')) {
      bengkungCode = 'kuning0';
      pangkatTitle = 'Jurulatih';
    }

    if (matched) {
      return {
        id: matched.id,
        name: matched.name,
        role: matched.role,
        bengkung: bengkungCode,
        bengkungBadge: matched.bengkungBadge || '',
        pangkat: pangkatTitle,
        photoUrl: matched.photoUrl || defaultPhoto,
        initials: getInitials(matched.name),
        titles: defaultTitles || '',
        gelanggang: matched.gelanggang || 'ASMM',
      };
    }

    return {
      id: '',
      name: defaultName,
      role: roleKeys[0],
      bengkung: bengkungCode,
      bengkungBadge: '',
      pangkat: pangkatTitle,
      photoUrl: defaultPhoto,
      initials: defaultInitials,
      titles: defaultTitles || '',
      gelanggang: 'ASMM',
    };
  };

  // Dynamically resolve each officer in the hierarchy
  const pengerusi = getMemberByRole(['Pengerusi'], "Mohd Hafife Bin Abd Halim", "MH", "/badges/hafife.jpeg", "PJK, PJM, PPC", "kuning5");
  const timbalan = getMemberByRole(['Timbalan Pengerusi'], "Zulkifli Bin Saad", "ZS", "/badges/zulkifli.jpeg", "", "kuning5");

  const setiausaha = getMemberByRole(['Setiausaha'], "Mohd Fauzi Bin Abd Aziz", "MF", "/badges/fauzi.jpeg", "", "kuning5");
  const penSetiausaha = getMemberByRole(['Pen. Setiausaha', 'Penolong Setiausaha'], "Mohammad Syah Firafizal Bin Abubakar", "MS", "/badges/syah-firafizal.jpeg", "", "merah3");
  const bendahari = getMemberByRole(['Bendahari'], "Mohd Aizat Bin Yusoff", "MA", "/badges/aizat.jpeg", "", "kuning0");

  const biroLatihan = getMemberByRole(['Ketua Biro Latihan & Dakwah'], "Hasbullah Bin Shaik Ahmad Amirruddin", "HA", "/badges/hasbullah.jpeg", "", "kuning5");
  const biroSosial = getMemberByRole(['Ketua Biro Sosial & Kebajikan'], "Mohamad Shakir Fahmi Bin Mohamad Zaki", "MS", "/badges/shakir.jpeg", "", "kuning5");
  const biroSukan = getMemberByRole(['Ketua Biro Keusahawanan & Sukan'], "Shahrul Aswad Bin Shamsuri", "SA", "/badges/aswad.jpeg", "", "merah2");
  const biroMedia = getMemberByRole(['Ketua Biro Media & Penerangan'], "Fikriz Zaki Bin Shaik Ahmad Amirruddin", "FZ", "/badges/zaki.jpeg", "", "kuning5");

  const allOfficers: OfficerItem[] = [
    pengerusi,
    timbalan,
    setiausaha,
    penSetiausaha,
    bendahari,
    biroLatihan,
    biroSosial,
    biroSukan,
    biroMedia,
  ];

  const selectedOfficer = selectedOfficerIndex !== null ? allOfficers[selectedOfficerIndex] : null;

  const handlePrevOfficer = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedOfficerIndex((prev) => {
      if (prev === null) return null;
      return (prev - 1 + allOfficers.length) % allOfficers.length;
    });
  };

  const handleNextOfficer = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedOfficerIndex((prev) => {
      if (prev === null) return null;
      return (prev + 1) % allOfficers.length;
    });
  };

  // User Rule: Opening modal always will lock body scroll + keyboard arrow navigation
  useEffect(() => {
    if (selectedOfficerIndex !== null) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setSelectedOfficerIndex(null);
        if (e.key === 'ArrowLeft') {
          setSelectedOfficerIndex((prev) => prev !== null ? (prev - 1 + allOfficers.length) % allOfficers.length : null);
        }
        if (e.key === 'ArrowRight') {
          setSelectedOfficerIndex((prev) => prev !== null ? (prev + 1) % allOfficers.length : null);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedOfficerIndex, allOfficers.length]);

  return (
    <div className="w-full space-y-8 min-h-[75vh] bg-white text-slate-900 pb-16">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-10 space-y-8">
        <div className="border-b border-slate-100 pb-5 text-center max-w-2xl mx-auto">
          <span className="inline-block px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold border border-red-100 mb-2">
            Tadbir Urus & Kepimpinan Induk
          </span>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Penstrukturan Jawatankuasa Induk PSSASMM
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1.5">
            Barisan kepimpinan rasmi berdaftar secara sah di bawah Jabatan Pendaftaran Pertubuhan Malaysia (ROS Kedah). Klik mana-mana kad profil untuk melihat foto bersaiz besar dan maklumat pangkat.
          </p>
        </div>

        {/* 4-Tier Org Chart Tree */}
        <div className="space-y-0 py-4">
          {/* TIER 1: PENGERUSI */}
          <div className="flex flex-col items-center relative pb-0">
            <div
              onClick={() => setSelectedOfficerIndex(0)}
              className="w-full max-w-xs p-5 bg-slate-900 text-white rounded-2xl border-t-4 border-amber-400 text-center shadow-md hover:-translate-y-1 hover:shadow-xl hover:ring-2 hover:ring-amber-400/50 transition-all duration-300 relative group cursor-pointer"
              title="Klik untuk lihat foto besar & maklumat pangkat"
            >
              {/* Zoom hint badge */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 backdrop-blur-xs p-1.5 rounded-full text-white shadow-xs">
                <ZoomIn className="w-3.5 h-3.5" />
              </div>

              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-400/40 mx-auto shadow-sm flex items-center justify-center bg-slate-800 relative">
                <img
                  src={pengerusi.photoUrl}
                  alt={pengerusi.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLElement).parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = 'w-full h-full flex items-center justify-center font-black text-amber-400 text-sm';
                      fallback.innerText = pengerusi.initials;
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              <h4 className="font-extrabold text-sm mt-3 uppercase tracking-tight">
                {pengerusi.name}
              </h4>
              {pengerusi.titles && (
                <p className="text-[10px] text-slate-400 font-semibold mt-0.5">
                  {pengerusi.titles}
                </p>
              )}
              <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
                <span className="inline-block bg-amber-400/20 text-amber-300 border border-amber-400/30 text-[10px] font-extrabold px-3 py-0.5 rounded-full uppercase tracking-wider">
                  {pengerusi.role}
                </span>
                <span className="inline-flex items-center gap-1 bg-white/10 text-slate-200 border border-white/20 text-[9px] font-bold px-2 py-0.5 rounded-full">
                  <Award className="w-2.5 h-2.5 text-amber-400" />
                  <span>{pengerusi.pangkat}</span>
                </span>
              </div>
            </div>
          </div>

          {/* TIER 2: TIMBALAN PENGERUSI */}
          <div className="flex flex-col items-center relative pt-16 pb-8">
            {/* Single continuous vertical connector line from Pengerusi to Timbalan (64px) */}
            <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-16 bg-slate-200" />

            <div
              onClick={() => setSelectedOfficerIndex(1)}
              className="w-full max-w-xs p-5 bg-white border border-slate-200 rounded-2xl border-t-4 border-blue-600 text-center shadow-sm hover:-translate-y-1 hover:shadow-lg hover:ring-2 hover:ring-blue-400/50 transition-all duration-300 relative group cursor-pointer"
              title="Klik untuk lihat foto besar & maklumat pangkat"
            >
              {/* Zoom hint badge */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/10 p-1.5 rounded-full text-slate-700 shadow-xs">
                <ZoomIn className="w-3.5 h-3.5" />
              </div>

              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-blue-500/30 mx-auto shadow-sm flex items-center justify-center bg-slate-100">
                <img
                  src={timbalan.photoUrl}
                  alt={timbalan.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLElement).parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = 'w-full h-full flex items-center justify-center font-bold text-blue-600 text-sm';
                      fallback.innerText = timbalan.initials;
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              <h4 className="font-extrabold text-sm text-slate-900 mt-3 uppercase tracking-tight">
                {timbalan.name}
              </h4>
              <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
                <span className="inline-block bg-blue-50 text-blue-600 border border-blue-100 text-[10px] font-extrabold px-3 py-0.5 rounded-full uppercase tracking-wider">
                  {timbalan.role}
                </span>
                <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-700 border border-slate-200 text-[9px] font-bold px-2 py-0.5 rounded-full">
                  <Award className="w-2.5 h-2.5 text-blue-600" />
                  <span>{timbalan.pangkat}</span>
                </span>
              </div>
            </div>
            {/* Vertical connector line going down to Tier 3 bridge */}
            <div className="hidden md:block absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200" />
          </div>

          {/* TIER 3: OFFICERS (SETIAUSAHA, PEN. SETIAUSAHA, BENDAHARI) */}
          <div className="relative pt-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[setiausaha, penSetiausaha, bendahari].map((officer, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  {/* Segmented horizontal bridge line with negative outer margins to bridge the gap-6 (24px) spacing */}
                  {i === 0 && (
                    <div className="hidden md:block absolute -top-8 -right-3 left-1/2 border-t-2 border-slate-200" />
                  )}
                  {i === 1 && (
                    <div className="hidden md:block absolute -top-8 -left-3 -right-3 border-t-2 border-slate-200" />
                  )}
                  {i === 2 && (
                    <div className="hidden md:block absolute -top-8 -left-3 right-1/2 border-t-2 border-slate-200" />
                  )}

                  {/* Vertical connector line from bridge to card */}
                  <div className="hidden md:block absolute -top-8 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200" />

                  <div
                    onClick={() => setSelectedOfficerIndex(2 + i)}
                    className="w-full p-5 bg-white border border-slate-200 rounded-2xl border-t-4 border-slate-800 text-center shadow-sm hover:-translate-y-1 hover:shadow-lg hover:ring-2 hover:ring-slate-400/50 transition-all duration-300 relative group cursor-pointer"
                    title="Klik untuk lihat foto besar & maklumat pangkat"
                  >
                    {/* Zoom hint badge */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/10 p-1.5 rounded-full text-slate-700 shadow-xs">
                      <ZoomIn className="w-3.5 h-3.5" />
                    </div>

                    <div className="w-14 h-14 rounded-full overflow-hidden border border-slate-200 mx-auto shadow-sm flex items-center justify-center bg-slate-100">
                      <img
                        src={officer.photoUrl}
                        alt={officer.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          const parent = (e.target as HTMLElement).parentElement;
                          if (parent) {
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full h-full flex items-center justify-center font-bold text-slate-700 text-xs';
                            fallback.innerText = officer.initials;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    </div>
                    <h4 className="font-extrabold text-xs sm:text-sm text-slate-900 mt-3 uppercase tracking-tight leading-tight min-h-[1.5rem] flex items-center justify-center">
                      {officer.name}
                    </h4>
                    <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
                      <span className="inline-block bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-extrabold px-3 py-0.5 rounded-full uppercase tracking-wider">
                        {officer.role}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold px-2 py-0.5 rounded-full">
                        <Award className="w-2.5 h-2.5 text-amber-600" />
                        <span>{officer.pangkat}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Vertical connector line from Tier 3 down to Tier 4 bridge */}
            <div className="hidden md:block absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200" />
          </div>

          {/* TIER 4: AJK BIASA & BIRO */}
          <div className="relative pt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
              {[biroLatihan, biroSosial, biroSukan, biroMedia].map((ajk, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  {/* Segmented horizontal bridge line with negative outer margins to bridge the gap-4 (16px) spacing */}
                  {i === 0 && (
                    <div className="hidden md:block absolute -top-8 -right-2 left-1/2 border-t-2 border-slate-200" />
                  )}
                  {(i === 1 || i === 2) && (
                    <div className="hidden md:block absolute -top-8 -left-2 -right-2 border-t-2 border-slate-200" />
                  )}
                  {i === 3 && (
                    <div className="hidden md:block absolute -top-8 -left-2 right-1/2 border-t-2 border-slate-200" />
                  )}

                  {/* Vertical connector line from bridge to card */}
                  <div className="hidden md:block absolute -top-8 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-slate-200" />

                  <div
                    onClick={() => setSelectedOfficerIndex(5 + i)}
                    className="w-full p-4 sm:p-5 bg-white border border-slate-200 rounded-2xl border-t-4 border-red-600 text-center shadow-sm hover:-translate-y-1 hover:shadow-lg hover:ring-2 hover:ring-red-400/50 transition-all duration-300 relative group cursor-pointer"
                    title="Klik untuk lihat foto besar & maklumat pangkat"
                  >
                    {/* Zoom hint badge */}
                    <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/10 p-1.5 rounded-full text-slate-700 shadow-xs">
                      <ZoomIn className="w-3 h-3" />
                    </div>

                    <div className="relative w-14 h-14 mx-auto">
                      <div className="w-full h-full rounded-full overflow-hidden border border-red-100 shadow-sm flex items-center justify-center bg-red-50/50">
                        <img
                          src={ajk.photoUrl}
                          alt={ajk.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLElement).parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'w-full h-full flex items-center justify-center font-bold text-red-600 text-xs';
                              fallback.innerText = ajk.initials;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <h4 className="font-extrabold text-[11px] sm:text-xs text-slate-900 mt-3 uppercase tracking-tight leading-tight min-h-[1.5rem] flex items-center justify-center">
                      {ajk.name}
                    </h4>
                    <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1">
                      <span className="inline-block bg-red-50 text-red-600 border border-red-100 text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        {ajk.role}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-700 border border-slate-200 text-[8px] font-bold px-2 py-0.5 rounded-full">
                        <Award className="w-2.5 h-2.5 text-red-600" />
                        <span>{ajk.pangkat}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Officer Full Profile & Large Photo Modal with Left/Right Navigation */}
      {selectedOfficer && selectedOfficerIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-officer-name"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/75 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setSelectedOfficerIndex(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Bar: Counter & Close */}
            <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
              <span className="bg-slate-900/70 text-white text-[11px] font-mono font-black px-3 py-1 rounded-full backdrop-blur-md border border-white/20 shadow-md">
                {selectedOfficerIndex + 1} / {allOfficers.length}
              </span>
            </div>

            <div className="absolute top-3 right-3 z-30">
              <button
                type="button"
                onClick={() => setSelectedOfficerIndex(null)}
                aria-label="Tutup"
                className="w-9 h-9 rounded-full bg-slate-900/60 hover:bg-slate-900 text-white backdrop-blur-md flex items-center justify-center transition cursor-pointer shadow-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Large Photo Section - Strict 1:1 Aspect Ratio with Left/Right Nav Controls */}
            <div className="w-full aspect-square bg-slate-900 flex items-center justify-center relative overflow-hidden shrink-0 group/photo">
              <img
                src={selectedOfficer.photoUrl}
                alt={selectedOfficer.name}
                className="w-full h-full object-cover transition-opacity duration-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLElement).parentElement;
                  if (parent) {
                    const fallback = document.createElement('div');
                    fallback.className = 'w-full h-full min-h-[220px] flex items-center justify-center font-black text-white text-5xl bg-slate-800';
                    fallback.innerText = selectedOfficer.initials;
                    parent.appendChild(fallback);
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />

              {/* Navigation: Previous Officer Arrow Button */}
              <button
                type="button"
                onClick={handlePrevOfficer}
                aria-label="Profil Sebelumnya"
                title="Profil Sebelumnya (Tekan Arrow Kiri)"
                className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-slate-900/70 hover:bg-slate-900 text-white backdrop-blur-md flex items-center justify-center transition cursor-pointer shadow-lg active:scale-95 border border-white/25 hover:border-white/50"
              >
                <ChevronLeft className="w-6 h-6 -ml-0.5" />
              </button>

              {/* Navigation: Next Officer Arrow Button */}
              <button
                type="button"
                onClick={handleNextOfficer}
                aria-label="Profil Seterusnya"
                title="Profil Seterusnya (Tekan Arrow Kanan)"
                className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-slate-900/70 hover:bg-slate-900 text-white backdrop-blur-md flex items-center justify-center transition cursor-pointer shadow-lg active:scale-95 border border-white/25 hover:border-white/50"
              >
                <ChevronRight className="w-6 h-6 -mr-0.5" />
              </button>

              <div className="absolute bottom-3 left-4 right-4 text-white pointer-events-none">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-amber-400" />
                  Barisan Kepimpinan PSSASMM
                </span>
              </div>
            </div>

            {/* Officer Details Card Content */}
            <div className="p-6 sm:p-7 space-y-4">
              <div>
                <h3 id="modal-officer-name" className="text-xl sm:text-2xl font-black text-slate-900 uppercase tracking-tight">
                  {selectedOfficer.name}
                </h3>
                {selectedOfficer.titles && (
                  <p className="text-xs font-semibold text-slate-500 mt-0.5">
                    {selectedOfficer.titles}
                  </p>
                )}
              </div>

              {/* Pangkat & Role Badges Grid */}
              <div className="space-y-2.5 pt-1 border-t border-slate-100">
                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0 mt-0.5 border border-red-100">
                    <Shield className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Jawatan Jawatankuasa</span>
                    <span className="font-extrabold text-slate-900 text-sm">{selectedOfficer.role}</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 mt-0.5 border border-amber-100">
                    <Award className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pangkat Persilatan / Bengkung</span>
                    <span className="font-extrabold text-amber-700 text-sm">{selectedOfficer.pangkat}</span>
                  </div>
                </div>

              </div>

              {/* Bottom Navigation Toolbar */}
              <div className="pt-2 flex items-center justify-between border-t border-slate-100 text-xs text-slate-500">
                <button
                  type="button"
                  onClick={handlePrevOfficer}
                  className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 py-1.5 px-3 rounded-xl transition cursor-pointer"
                  title="Profil Sebelumnya (Tekan Arrow Kiri)"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Sebelum</span>
                </button>

                <div className="flex items-center gap-1">
                  {allOfficers.map((off, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedOfficerIndex(idx)}
                      title={off.name}
                      aria-label={off.name}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${
                        idx === selectedOfficerIndex ? 'w-5 bg-red-600' : 'w-1.5 bg-slate-200 hover:bg-slate-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleNextOfficer}
                  className="inline-flex items-center gap-1 font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 py-1.5 px-3 rounded-xl transition cursor-pointer"
                  title="Profil Seterusnya (Tekan Arrow Kanan)"
                >
                  <span>Seterusnya</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default OrgChartPage;

